import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Case-convention bridge. The backend speaks snake_case (DB columns); some pages
 * were written against camelCase. To avoid brittle per-field mapping, every
 * object key is aliased to BOTH camelCase and snake_case — on request bodies and
 * query params (the backend's zod schemas strip the unused alias) and on response
 * data (pages can read either convention). Idempotent and non-destructive.
 */
const toSnake = (k) => k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
const toCamel = (k) => k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

function aliasKeys(value) {
  if (Array.isArray(value)) return value.map(aliasKeys);
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const aliased = aliasKeys(v);
      out[k] = aliased;
      const snake = toSnake(k);
      const camel = toCamel(k);
      if (!(snake in value) && out[snake] === undefined) out[snake] = aliased;
      if (!(camel in value) && out[camel] === undefined) out[camel] = aliased;
    }
    return out;
  }
  return value;
}

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success (use requestEnvelope
 *   when you also need pagination metadata like nextCursor).
 * - Throws an Error carrying { status, details } on failure.
 */
class ApiService {
  /** `raw: true` skips the key aliasing (for payloads whose keys are data, e.g. mapping tables). */
  async requestEnvelope(endpoint, { method = 'GET', body, headers, query, raw = false } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const url = API_BASE_URL + endpoint + buildQuery(query ? aliasKeys(query) : query);

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(raw ? body : aliasKeys(body)) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    let payload = isJson ? await res.json() : null;
    // Alias response data so pages can read either camelCase or snake_case.
    if (!raw && payload && typeof payload === 'object' && 'data' in payload) {
      payload = { ...payload, data: aliasKeys(payload.data) };
    }

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }
    return payload;
  }

  /** Returns just the unwrapped `data`. */
  async request(endpoint, opts) {
    const payload = await this.requestEnvelope(endpoint, opts);
    return payload?.data ?? payload;
  }

  // ---- Generic verbs ----
  get(endpoint, query) {
    return this.request(endpoint, { method: 'GET', query });
  }
  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body });
  }
  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body });
  }
  patch(endpoint, body) {
    return this.request(endpoint, { method: 'PATCH', body });
  }
  del(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * Keyset list fetch: returns { data, nextCursor } so callers can page.
   * `params` = { limit, cursor, search, from, to, ...filters }.
   */
  async list(endpoint, params = {}) {
    const payload = await this.requestEnvelope(endpoint, { method: 'GET', query: params });
    return { data: payload?.data ?? [], nextCursor: payload?.nextCursor ?? null };
  }

  // ---- Auth ----
  contractorSignup(body) {
    return this.request('/portal/signup', { method: 'POST', body });
  }
  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }
  me() {
    return this.request('/auth/me');
  }
  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }
  verifyOtp({ email, otp }) {
    return this.request('/auth/verify-otp', { method: 'POST', body: { email, otp } });
  }
  resetPassword({ email, otp, newPassword }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { email, otp, newPassword } });
  }
}

/** Build a query string from an object, dropping null/undefined/empty values. */
function buildQuery(query) {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue;
    sp.append(k, v instanceof Date ? v.toISOString() : v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export default new ApiService();
